import getGifFromFrames from '../../utils/get-gif-from-frames'
import downloadFile from 'downloadjs'
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useBeforeUnload } from 'react-use'
import { WEB_API_KEY } from 'shared/api'
import { SuggestedTagEvent } from 'shared/components/suggested-tags'
import { AccountsManagerContext } from 'shared/contexts/accounts-manager'
import MessageContext from 'shared/contexts/message'
import UserContext from 'shared/contexts/user'
import { encodeGAParams } from 'shared/util/analytics'
import { MAX_TAG_LIMIT } from '../../constants'
import { CreationToolsContext } from '../../manager'
import { getBasePathFromCreationTool } from '../../routes'
import { CreationToolSourceFile } from '../../types'
import CreationTool from '../../types/creation-tool'
import CreationToolError from '../../types/error'
import CreationToolFileType from '../../types/file-type'
import CreationToolOutputType from '../../types/output-type'
import CreationToolStatus from '../../types/status'
import getUploadWithEdits from '../../utils/get-upload-with-edits'
import { uploadGif } from '../../utils/upload'
import {
    CreationToolUploadContextProps,
    CreationToolUploadEditing,
    CreationToolUploadSource,
    CreationToolUploadTag,
} from './types'
import { validateSourceByOutput } from '../../utils/validate-source'

export const CreationToolUploadContext = createContext({} as CreationToolUploadContextProps)

const cleanUpTags = (tags: CreationToolUploadTag[]): CreationToolUploadTag[] => {
    let cleaned: CreationToolUploadTag[] = []

    tags.forEach((tag) => {
        const cleanedTag = tag.text.replace(/#/g, '').trim()
        const existingIndex = cleaned.findIndex((t) => t.text === cleanedTag)
        if (existingIndex > -1) {
            cleaned[existingIndex] = { ...tag, text: cleanedTag }
        } else {
            cleaned.push({ ...tag, text: cleanedTag })
        }
    })

    return cleaned
}

type Props = {
    children: ReactNode
    submitUrl: string
    apiKey?: string
}

export const convertSource = (source: CreationToolSourceFile, outputType: CreationToolOutputType) => ({
    id: source.id,
    source,
    tags: (source.tags || []).map((tag) => ({ text: tag })),
    status: CreationToolStatus.Idle,
    title: source.title,
    sourceUrl: source.sourceUrl,
    outputType:
        outputType === CreationToolOutputType.Gif && source.isTransparent
            ? CreationToolOutputType.Sticker // if uploading as a gif, and the gif is transparent, show sticker as output type
            : outputType,
})

const CreationToolUploadContextManager = ({ children, submitUrl, apiKey }: Props) => {
    const { sources, trackEvent, tool, stepFrames, outputType, setSources } = useContext(CreationToolsContext)
    const { user } = useContext(UserContext)
    const { channel_access } = useContext(AccountsManagerContext)
    const { sendMessage } = useContext(MessageContext)
    const history = useHistory()
    const source = sources.length ? sources[0] : null
    const [sourceUrl, setSourceUrl] = useState<string>(source?.sourceUrl || '')
    const [tags, setTags] = useState<CreationToolUploadTag[]>((source?.tags || []).map((tag) => ({ text: tag })))
    const [rating, setRating] = useState<string>(source?.rating || '')
    const [channelId, setChannelId] = useState<string>('')
    const [username, setUsername] = useState<string>(source?.username || user.username || '')
    const [description, setDescription] = useState<string>(source?.description || '')
    const [ctaText, setCtaText] = useState<string>('')
    const [isPublic, setIsPublic] = useState<boolean>(true)
    const [status, setStatus] = useState<CreationToolStatus>(CreationToolStatus.Idle)
    const [editing, setEditing] = useState<CreationToolUploadEditing | undefined>()
    const [suggestedTagEventBuffer, setSuggestedTagEventBuffer] = useState<SuggestedTagEvent[]>([])
    const pausedRef = useRef<boolean>(false)
    const failedUploadIdsRef = useRef<string[]>([])

    const gaCategory = CreationTool.gaEventCategory(tool)
    const gaAction = CreationToolOutputType.title(outputType)

    const [uploadSources, setUploadSources] = useState<CreationToolUploadSource[]>(
        sources.map((s) => convertSource(s, outputType))
    )

    useEffect(() => {
        const updateSources = async () => {
            const filterExisting = (source: CreationToolSourceFile) =>
                uploadSources.findIndex((existingSource) => existingSource.id === source.id) === -1
            const newSources = await Promise.all(
                sources.filter(filterExisting).map((s) => convertSource(s, outputType))
            )
            if (newSources.length > 0) {
                // only update new sources, since uploadSource items can be edited
                setUploadSources([...uploadSources, ...newSources])
            }
        }
        updateSources()
    }, [sources, outputType])

    // page reload warning
    useBeforeUnload(
        status !== CreationToolStatus.Complete && stepFrames.length > 0,
        `You'll lose all your beautiful work!`
    )

    const hasReachTagLimit = (tags: CreationToolUploadTag[]) => {
        const filteredTags = tags.filter((tag) => tag.text !== 'transparent')
        return filteredTags.length > MAX_TAG_LIMIT
    }

    const addTags = (newTags: CreationToolUploadTag[], sourceId?: string): boolean => {
        let tag = newTags[0].text
        let errorMessage = `Oops - you've reached the limit of ${MAX_TAG_LIMIT} tags per upload. To add #${tag}, please remove others.`
        // update specific upload source
        if (sourceId) {
            const source = uploadSources.find((src) => src.id === sourceId)
            if (source) {
                let newTagList = cleanUpTags([...source.tags, ...newTags])
                let allTags = cleanUpTags([...newTagList, ...tags])
                if (hasReachTagLimit(allTags)) {
                    sendMessage(errorMessage, 'tag_error')
                    return false
                }
                updateUploadSource({
                    ...source,
                    tags: newTagList,
                })
            }
        } else {
            let newTagList = cleanUpTags([...tags, ...newTags])
            if (uploadSources.length > 1) {
                let result = uploadSources.reduce((count, source) => {
                    let allTags = cleanUpTags([...newTagList, ...source.tags])
                    let currentCount = Number(hasReachTagLimit(allTags))
                    return count + currentCount
                }, 0)
                if (result > 0) {
                    let message = `#${tag} was added to ${uploadSources.length - result} of the ${
                        uploadSources.length
                    } uploads.
                    ${result} item(s) have reached the limit of ${MAX_TAG_LIMIT} tags.`
                    sendMessage(message, 'tag_warn')
                }
            } else {
                if (hasReachTagLimit(newTagList) && newTags.length === 1) {
                    sendMessage(errorMessage, 'tag_error')
                    return false
                }
            }
            setTags(newTagList)
        }
        return true
    }

    const removeTags = (removedTags: string[], sourceId?: string) => {
        // update specific upload source
        if (sourceId) {
            const source = uploadSources.find((src) => src.id === sourceId)
            if (source) {
                updateUploadSource({
                    ...source,
                    tags: source.tags.filter(({ text }) => removedTags.indexOf(text) < 0),
                })
            }
        } else {
            setTags((tags) => tags.filter(({ text }) => removedTags.indexOf(text) < 0))
        }
    }

    const addUploadSource = (source: CreationToolUploadSource, index?: number) => {
        const updatedSources = [...uploadSources]
        if (typeof index === 'number') {
            updatedSources.splice(index, 0, source)
        } else {
            updatedSources.push(source)
        }
        setUploadSources(updatedSources)
    }

    const updateUploadSource = (source: CreationToolUploadSource) => {
        setUploadSources((uploadSources) =>
            uploadSources.map((src) => {
                return src.id === source.id ? source : src
            })
        )
    }

    const removeUploadSource = (sourceId: string) => {
        const updatedSources = uploadSources.filter((src) => src.id !== sourceId)
        if (updatedSources.length) {
            // also remove this from the main sources
            setSources(sources.filter((src) => src.id !== sourceId))
            setUploadSources(updatedSources)
        } else {
            // redirect to welcome screen since there are no more sources left
            history.replace(getBasePathFromCreationTool(tool))
        }
    }

    const pause = () => {
        pausedRef.current = true
        setStatus(CreationToolStatus.Idle)
    }

    const upload = async (uploads: CreationToolUploadSource[]) => {
        let generatedFile: CreationToolSourceFile | null = null
        let successfulUploadCount = 0

        // unset paused state
        pausedRef.current = false

        // if there are frames and only one source, generate a gif from the frames (used for sticker maker)
        // This is only for static sources
        if (uploads.length === 1 && stepFrames.length > 0 && !source?.isAnimated) {
            setStatus(CreationToolStatus.Generating)
            const gif = await getGifFromFrames(stepFrames)
            generatedFile = await validateSourceByOutput(gif, uploads[0].outputType)
        }

        // set the global status of uploading
        setStatus(CreationToolStatus.Uploading)

        const filteredUploads = uploads.filter(
            (src) => !src.error && !src.uploadedId && src.status === CreationToolStatus.Idle
        )

        for (let i = 0; i < filteredUploads.length; i++) {
            const upload = filteredUploads[i]
            const { source: src, edits } = upload

            // stop if paused
            if (pausedRef.current) {
                break
            }

            // update the status on the upload source
            updateUploadSource({ ...upload, status: CreationToolStatus.Uploading })
            trackEvent(gaCategory, 'Upload', 'Start', gaAction)

            try {
                const isCreatedSticker = upload.isDuplicate && upload.outputType === CreationToolOutputType.Sticker
                const creationLocationPrefix = CreationTool.creationLocationPrefix(
                    tool,
                    uploads.length > 1,
                    isCreatedSticker
                )
                const creationLocationSuffix = CreationToolOutputType.creationLocationSuffix(
                    upload.outputType || outputType
                )
                const uploadTags = [...tags, ...upload.tags]
                // use token of original user if emulation is enabled
                const userToken = channel_access?.user?.token || user.userToken

                const uploadedId = await uploadGif(submitUrl, generatedFile || src, {
                    apiKey: apiKey || WEB_API_KEY,
                    outputType: upload.outputType || outputType,
                    title: upload.title,
                    description: upload.description || description,
                    tags: [...uploadTags, { text: CreationTool.uploadTag(tool) }],
                    creationLocation: `${creationLocationPrefix}_${creationLocationSuffix}`,
                    sourceUrl: upload.sourceUrl || sourceUrl,
                    edits,
                    ctaText,
                    rating,
                    channelId,
                    username,
                    isPublic,
                    userToken,
                })

                // fire buffered suggested tag events
                suggestedTagEventBuffer.forEach((e) => {
                    trackEvent(
                        gaCategory,
                        e.action,
                        encodeGAParams({
                            gif_id: uploadedId,
                            tag_name: e.tag.name,
                            tag_source: e.tag.source,
                            response_id: e.tag.response_id,
                            user_name: username,
                        })
                    )
                })

                trackEvent(gaCategory, 'Upload', 'Success', gaAction)
                successfulUploadCount++

                if (uploadSources.length === 1) {
                    setStatus(CreationToolStatus.Complete)
                    // redirect to the upload if not bulk
                    setTimeout(() => {
                        location.href = `${CreationToolOutputType.uploadedBasePath(upload.outputType)}/${uploadedId}`
                    }, 300)
                } else {
                    updateUploadSource({ ...upload, uploadedId, status: CreationToolStatus.Idle })
                }
            } catch (err) {
                let error = CreationToolError.fromServerResponse(err)

                // check if error is upload failed and is second time
                if (error === CreationToolError.UploadFailed && failedUploadIdsRef.current.indexOf(upload.id) > -1) {
                    error = CreationToolError.UploadFailedAgain
                }

                // display error banner
                const errorText = CreationToolError.description(error)
                sendMessage(errorText, 'error')

                // if bulk, add error to individual item.
                // otherwise reset everything.
                if (uploadSources.length > 1) {
                    updateUploadSource({ ...upload, status: CreationToolStatus.Idle, error })
                } else {
                    updateUploadSource({ ...upload, status: CreationToolStatus.Idle })
                    setStatus(CreationToolStatus.Idle)
                }

                failedUploadIdsRef.current.push(upload.id)
            }
        }

        if (uploadSources.length > 1) {
            setStatus(CreationToolStatus.Idle)
        }

        if (successfulUploadCount > 0) {
            sendMessage('Upload Complete!', 'success')
        }
    }

    const download = async () => {
        let file: File | string
        let generatedFile: CreationToolSourceFile | null = null
        let filename = 'giphy.gif'

        console.log('comecou o download')

        trackEvent(gaCategory, 'Download', 'Start', gaAction)
        try {
            setStatus(CreationToolStatus.Generating)
            const { source, edits } = uploadSources[0]

            // if there are frames and only one source, generate a gif from the frames (used for sticker maker)
            // This is only for static sources
            if (uploadSources.length === 1 && stepFrames.length > 0 && !source?.isAnimated) {
                const gif = await getGifFromFrames(stepFrames)
                generatedFile = await validateSourceByOutput(gif, uploadSources[0].outputType)
            }

            const sourceIsVideo = CreationToolFileType.isVideo(source.type)
            const outputIsVideo = !!(sourceIsVideo && (!edits?.frames || edits.frames.length < 2))
            filename = outputIsVideo && stepFrames.length === 0 ? 'giphy.mp4' : filename
            filename = outputIsVideo ? 'giphy.mp4' : filename
            file = await getUploadWithEdits(
                generatedFile || source,
                outputIsVideo ? CreationToolOutputType.Video : CreationToolOutputType.Gif,
                edits
            )

            setStatus(CreationToolStatus.Downloading)
            downloadFile(file, filename)
            setStatus(CreationToolStatus.Idle)
            trackEvent(gaCategory, 'Download', 'Success', gaAction)
        } catch (error) {
            sendMessage(`Error downloading your file :(`, 'error')
            setStatus(CreationToolStatus.Idle)
        }
    }

    return (
        <CreationToolUploadContext.Provider
            value={{
                allowDownload: uploadSources.length === 1,
                uploadSources,
                status,
                tags,
                sourceUrl,
                rating,
                description,
                channelId,
                username,
                isPublic,
                ctaText,
                editing,
                addTags,
                removeTags,
                addUploadSource,
                updateUploadSource,
                removeUploadSource,
                setSuggestedTagEventBuffer,
                setSourceUrl,
                setRating,
                setDescription,
                setChannelId,
                setUsername,
                setStatus,
                setIsPublic,
                setCtaText,
                setEditing,
                upload,
                download,
                pause,
            }}
        >
            {children}
        </CreationToolUploadContext.Provider>
    )
}

export default CreationToolUploadContextManager
